// R58-L-13 — Viewer header toggle controls.
//
// Two icon toggle buttons floated in the Viewer cell's top-left corner:
//   * "Show AI-finding bboxes" — pressed state mirrors the AI overlay
//     visibility; coloured swatch uses the `intent.info` Apple Signal
//     Blue (R58-41 lock: AI bboxes default to blue, not severity).
//   * "Show defect bboxes" — pressed state mirrors the defect overlay
//     visibility; coloured swatch uses `severity.high` orange as the
//     family colour for the per-bbox severity range (the actual stroke
//     is still per-bbox severity-coded by the Viewer; the toggle swatch
//     just hints at the kind).
//
// Both buttons are MUI `<ToggleButton>`-style — semantic `aria-pressed`
// + role="button" so Playwright can assert `aria-pressed="true|false"`
// against the canonical query (`getByRole("button", { name: ... })`).
//
// SB6-ANA-VIEW-05 — this no longer owns positioning or the `role="toolbar"`
// container. It now renders a bare button group and is composed inside the
// single draggable `ViewerToolbar`; the parent owns position + z-index.
//
// SB6-ANA-VIEW-05 follow-up — the AI / defect icon colours are no longer
// hardcoded. They come in as props sourced from the user's Settings →
// Bounding Boxes preference (`boundingBox.ai.color` / `boundingBox.defect.color`),
// so the icons match the actual bbox stroke colour and recolour live when the
// user changes it.
import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import { useBBoxToggles } from "./useBBoxToggles.js";
import { EyeIcon, EyeOffIcon } from "@aeriosiq/ui/icons";

export interface BBoxToggleControlsProps {
  /** AI-finding bbox colour — `boundingBox.ai.color` from Lyse preferences. */
  aiColor: string;
  /** Defect bbox colour — `boundingBox.defect.color` from Lyse preferences. */
  defectColor: string;
}

export function BBoxToggleControls({
  aiColor,
  defectColor,
}: BBoxToggleControlsProps): React.ReactElement {
  const aiVisible = useBBoxToggles((s) => s.aiVisible);
  const defectVisible = useBBoxToggles((s) => s.defectVisible);
  const toggleAi = useBBoxToggles((s) => s.toggleAi);
  const toggleDefect = useBBoxToggles((s) => s.toggleDefect);

  return (
    <Box component="span" aria-label="Viewer bbox toggles">
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Tooltip
          title={
            aiVisible ? "Hide AI-finding bboxes" : "Show AI-finding bboxes"
          }
        >
          <IconButton
            size="small"
            aria-pressed={aiVisible}
            aria-label="Show AI-finding bboxes"
            onClick={toggleAi}
            sx={{
              color: aiVisible ? aiColor : "text.secondary",
              border: aiVisible
                ? `1px solid ${aiColor}`
                : "1px solid transparent",
              borderRadius: 1,
            }}
          >
            {aiVisible ? (
              <EyeIcon fontSize="small" />
            ) : (
              <EyeOffIcon fontSize="small" />
            )}
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                display: "inline-block",
                width: 10,
                height: 10,
                ml: 0.5,
                borderRadius: 0.5,
                backgroundColor: aiColor,
                opacity: aiVisible ? 1 : 0.4,
              }}
            />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={defectVisible ? "Hide defect bboxes" : "Show defect bboxes"}
        >
          <IconButton
            size="small"
            aria-pressed={defectVisible}
            aria-label="Show defect bboxes"
            onClick={toggleDefect}
            sx={{
              color: defectVisible ? defectColor : "text.secondary",
              border: defectVisible
                ? `1px solid ${defectColor}`
                : "1px solid transparent",
              borderRadius: 1,
            }}
          >
            {defectVisible ? (
              <EyeIcon fontSize="small" />
            ) : (
              <EyeOffIcon fontSize="small" />
            )}
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                display: "inline-block",
                width: 10,
                height: 10,
                ml: 0.5,
                borderRadius: 0.5,
                backgroundColor: defectColor,
                opacity: defectVisible ? 1 : 0.4,
              }}
            />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}
